import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

import i18n from "../locale/i18n";
import { navigateTo } from "../routers/routeUtils";
import APP_ROUTES from "../routers/routeConstants";
import { auth } from "../firebase/firebase";

const apiUrl = process.env.REACT_APP_API_URL as string;
const httpClient = axios.create();

// Store the token interceptor ID so we can replace it
let tokenInterceptorId: number | null = null;

httpClient.interceptors.request.use((config) => {
  config.baseURL = apiUrl;
  // config.timeout = 10000;
  return config;
});

export const addAccessTokenInterceptor = (token: string) => {
  // Remove existing token interceptor if it exists
  if (tokenInterceptorId !== null) {
    httpClient.interceptors.request.eject(tokenInterceptorId);
  }

  // Add new token interceptor
  tokenInterceptorId = httpClient.interceptors.request.use((config) => {
    if (config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers["accept-language"] = i18n.language || "en";
    }
    return config;
  });
};

// Global error handling interceptor
httpClient.interceptors.response.use(
  (response) => {
    // Handle successful responses
    const { data } = response;

    if (data.message && data.message !== "success") {
      //   toast.success(data.message); // Display the message as a success toast
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 403 Forbidden errors - likely token expiration
    if (error.response?.status === 403 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Get the current user and refresh the token
        const currentUser = auth.currentUser;
        if (currentUser) {
          // Force refresh the token
          const newToken = await currentUser.getIdToken(true);

          // Update the authorization header with the new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          // Update the token interceptor with the new token
          addAccessTokenInterceptor(newToken);

          // Retry the original request with the new token
          return httpClient(originalRequest);
        }
      } catch (refreshError) {
        // If token refresh fails, reject the promise
        console.error("Token refresh failed:", refreshError);
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors globally and display a toast notification
    if (error.response) {
      const { status, data } = error.response;

      // Check if errors array is present in the error response
      if (data && typeof data === "object" && "errors" in data && Array.isArray(data.errors)) {
        (data.errors as string[]).forEach((errMsg: string) => {
          //   toast.error(errMsg);
        });
      }
    } else if (!error.response) {
      console.error("error", error);
      navigateTo(APP_ROUTES.NETWORK_ERROR);
    }

    return Promise.reject(error);
  }
);

export default httpClient;
